<template>
  <div class="user-profile-demo">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-purple-500 to-pink-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-account-circle" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">👤 UserProfile Component Demo</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Perfiles de usuario adaptativos por rol</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔧 Demo Controls -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedUser"
              :items="availableUsers"
              item-title="name"
              item-value="id"
              label="Seleccionar Usuario"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-account-switch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="profileLayout"
              :items="layoutOptions"
              item-title="label"
              item-value="value"
              label="Layout del Perfil"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="showDetailedInfo"
              label="Información Detallada"
              color="primary"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="editMode"
              label="Modo Edición"
              color="primary"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 👤 User Profile Component -->
    <v-row>
      <v-col cols="12" :md="profileLayout === 'sidebar' ? 4 : 12">
        <v-card elevation="2" class="user-profile-card">
          <!-- Profile Header -->
          <div class="profile-header pa-6 text-center text-white position-relative">
            <div class="profile-bg"></div>
            <div class="profile-content position-relative">
              <v-avatar size="120" class="mb-4 elevation-4">
                <v-img :src="currentUser?.avatar || '/api/placeholder/120/120'" />
              </v-avatar>
              
              <div v-if="!editMode">
                <h2 class="text-h4 mb-1">{{ currentUser?.name }}</h2>
                <p class="text-subtitle-1 mb-2 opacity-90">{{ currentUser?.role }}</p>
                <v-chip
                  :color="getRoleColor(currentUser?.role)"
                  variant="flat"
                  size="small"
                  class="mb-2"
                >
                  <v-icon :icon="getRoleIcon(currentUser?.role)" size="14" class="me-1" />
                  {{ currentUser?.department }}
                </v-chip>
              </div>

              <div v-else>
                <v-text-field
                  v-model="editableUser.name"
                  label="Nombre completo"
                  variant="outlined"
                  density="compact"
                  dark
                  class="mb-2"
                />
                <v-select
                  v-model="editableUser.role"
                  :items="roleOptions"
                  label="Rol"
                  variant="outlined"
                  density="compact"
                  dark
                  class="mb-2"
                />
              </div>

              <div class="d-flex justify-center gap-2 mt-4">
                <v-btn
                  v-if="!editMode"
                  variant="outlined"
                  color="white"
                  size="small"
                  prepend-icon="mdi-pencil"
                  @click="toggleEditMode"
                >
                  Editar
                </v-btn>
                <template v-else>
                  <v-btn
                    variant="flat"
                    color="success"
                    size="small"
                    prepend-icon="mdi-check"
                    @click="saveChanges"
                  >
                    Guardar
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    color="white"
                    size="small"
                    prepend-icon="mdi-close"
                    @click="cancelEdit"
                  >
                    Cancelar
                  </v-btn>
                </template>
              </div>
            </div>
          </div>

          <!-- Profile Stats -->
          <v-card-text class="pa-4">
            <v-row class="text-center">
              <v-col cols="4" v-for="stat in profileStats" :key="stat.label">
                <div>
                  <h3 class="text-h6 mb-1">{{ stat.value }}</h3>
                  <p class="text-caption text-medium-emphasis">{{ stat.label }}</p>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" :md="profileLayout === 'sidebar' ? 8 : 12">
        <v-row>
          <!-- Contact Information -->
          <v-col cols="12" :md="profileLayout === 'tabs' ? 6 : 12">
            <v-card elevation="1" class="mb-4">
              <v-card-title class="pa-4 bg-surface-variant">
                <v-icon icon="mdi-contact-mail" class="me-2" />
                Información de Contacto
              </v-card-title>
              <v-card-text class="pa-4">
                <v-list density="compact">
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-email" color="primary" />
                    </template>
                    <v-list-item-title>Email</v-list-item-title>
                    <v-list-item-subtitle>{{ currentUser?.email }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-phone" color="primary" />
                    </template>
                    <v-list-item-title>Teléfono</v-list-item-title>
                    <v-list-item-subtitle>{{ currentUser?.phone || 'No especificado' }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-map-marker" color="primary" />
                    </template>
                    <v-list-item-title>Ubicación</v-list-item-title>
                    <v-list-item-subtitle>{{ currentUser?.location || 'No especificado' }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Work Information -->
          <v-col cols="12" :md="profileLayout === 'tabs' ? 6 : 12">
            <v-card elevation="1" class="mb-4">
              <v-card-title class="pa-4 bg-surface-variant">
                <v-icon icon="mdi-briefcase" class="me-2" />
                Información Laboral
              </v-card-title>
              <v-card-text class="pa-4">
                <v-list density="compact">
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-office-building" color="success" />
                    </template>
                    <v-list-item-title>Departamento</v-list-item-title>
                    <v-list-item-subtitle>{{ currentUser?.department }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-calendar" color="success" />
                    </template>
                    <v-list-item-title>Fecha de Ingreso</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(currentUser?.startDate) }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-account-supervisor" color="success" />
                    </template>
                    <v-list-item-title>Supervisor</v-list-item-title>
                    <v-list-item-subtitle>{{ currentUser?.supervisor || 'No asignado' }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Permissions & Access -->
          <v-col cols="12" v-if="showDetailedInfo">
            <v-card elevation="1">
              <v-card-title class="pa-4 bg-surface-variant">
                <v-icon icon="mdi-shield-key" class="me-2" />
                Permisos y Accesos
              </v-card-title>
              <v-card-text class="pa-4">
                <div class="mb-4">
                  <h4 class="text-subtitle-1 mb-2">Permisos del rol:</h4>
                  <v-chip-group>
                    <v-chip
                      v-for="permission in currentUser?.permissions"
                      :key="permission"
                      size="small"
                      variant="outlined"
                      :color="getRoleColor(currentUser?.role)"
                    >
                      {{ permission }}
                    </v-chip>
                  </v-chip-group>
                </div>

                <div class="mb-4">
                  <h4 class="text-subtitle-1 mb-2">Módulos accesibles:</h4>
                  <v-list density="compact">
                    <v-list-item
                      v-for="module in currentUser?.accessibleModules"
                      :key="module.name"
                    >
                      <template #prepend>
                        <v-icon 
                          :icon="module.icon" 
                          :color="module.hasAccess ? 'success' : 'error'" 
                        />
                      </template>
                      <v-list-item-title>{{ module.name }}</v-list-item-title>
                      <template #append>
                        <v-chip
                          :color="module.hasAccess ? 'success' : 'error'"
                          size="small"
                          variant="flat"
                        >
                          {{ module.hasAccess ? 'Acceso' : 'Sin acceso' }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- 🔄 Back Navigation -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center/components')"
        >
          Volver a Componentes
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-code-tags"
          @click="showCode"
        >
          Ver Código
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface User {
  id: string
  name: string
  email: string
  phone?: string
  location?: string
  role: string
  department: string
  avatar?: string
  startDate: Date
  supervisor?: string
  permissions: string[]
  accessibleModules: {
    name: string
    icon: string
    hasAccess: boolean
  }[]
}

// 🔧 Reactive state
const store = useMainStore()
const selectedUser = ref('1')
const profileLayout = ref('sidebar')
const showDetailedInfo = ref(true)
const editMode = ref(false)
const editableUser = ref<Partial<User>>({})

// 📊 Mock data
const layoutOptions = ref([
  { label: 'Sidebar', value: 'sidebar' },
  { label: 'Tabs', value: 'tabs' },
  { label: 'Full Width', value: 'full' }
])

const roleOptions = ref([
  'Super Admin',
  'Gerente Regional',
  'Supervisor de Zona',
  'Operador de Rutas',
  'Empleado de Agencia',
  'Analista Central'
])

const mockUsers = ref<User[]>([
  {
    id: '1',
    name: 'Carlos Rodríguez',
    email: 'carlos.rodriguez@mgi.com',
    phone: '+1 (555) 123-4567',
    location: 'Zona Norte, República Dominicana',
    role: 'Super Admin',
    department: 'Administración Central',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    startDate: new Date('2020-03-15'),
    supervisor: 'Director General',
    permissions: ['create', 'read', 'update', 'delete', 'admin'],
    accessibleModules: [
      { name: 'Dashboard', icon: 'mdi-view-dashboard', hasAccess: true },
      { name: 'Usuarios', icon: 'mdi-account-group', hasAccess: true },
      { name: 'Incidencias', icon: 'mdi-alert-circle', hasAccess: true },
      { name: 'Reportes', icon: 'mdi-chart-line', hasAccess: true },
      { name: 'Configuración', icon: 'mdi-cog', hasAccess: true }
    ]
  },
  {
    id: '2',
    name: 'María González',
    email: 'maria.gonzalez@mgi.com',
    phone: '+1 (555) 234-5678',
    location: 'Región Central',
    role: 'Gerente Regional',
    department: 'Gestión Regional',
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
    startDate: new Date('2021-01-20'),
    supervisor: 'Carlos Rodríguez',
    permissions: ['read', 'update', 'manage_team'],
    accessibleModules: [
      { name: 'Dashboard', icon: 'mdi-view-dashboard', hasAccess: true },
      { name: 'Usuarios', icon: 'mdi-account-group', hasAccess: true },
      { name: 'Incidencias', icon: 'mdi-alert-circle', hasAccess: true },
      { name: 'Reportes', icon: 'mdi-chart-line', hasAccess: true },
      { name: 'Configuración', icon: 'mdi-cog', hasAccess: false }
    ]
  },
  {
    id: '3',
    name: 'Juan Pérez',
    email: 'juan.perez@mgi.com',
    phone: '+1 (555) 345-6789',
    location: 'Zona Sur',
    role: 'Operador de Rutas',
    department: 'Recolección',
    avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
    startDate: new Date('2022-06-10'),
    supervisor: 'María González',
    permissions: ['read', 'update_routes'],
    accessibleModules: [
      { name: 'Dashboard', icon: 'mdi-view-dashboard', hasAccess: true },
      { name: 'Usuarios', icon: 'mdi-account-group', hasAccess: false },
      { name: 'Incidencias', icon: 'mdi-alert-circle', hasAccess: true },
      { name: 'Reportes', icon: 'mdi-chart-line', hasAccess: false },
      { name: 'Configuración', icon: 'mdi-cog', hasAccess: false }
    ]
  }
])

const availableUsers = computed(() => 
  mockUsers.value.map(user => ({
    id: user.id,
    name: `${user.name} (${user.role})`
  }))
)

const currentUser = computed(() => 
  mockUsers.value.find(user => user.id === selectedUser.value)
)

const profileStats = computed(() => {
  if (!currentUser.value) return []
  
  const stats = [
    { label: 'Incidencias', value: '24' },
    { label: 'Reportes', value: '156' },
    { label: 'Años', value: Math.floor((Date.now() - currentUser.value.startDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)).toString() }
  ]
  
  return stats
})

// 🎯 Methods
const getRoleColor = (role?: string) => {
  const colors: Record<string, string> = {
    'Super Admin': 'red',
    'Gerente Regional': 'purple',
    'Supervisor de Zona': 'blue',
    'Operador de Rutas': 'orange',
    'Empleado de Agencia': 'green',
    'Analista Central': 'teal'
  }
  return colors[role || ''] || 'grey'
}

const getRoleIcon = (role?: string) => {
  const icons: Record<string, string> = {
    'Super Admin': 'mdi-shield-crown',
    'Gerente Regional': 'mdi-account-tie',
    'Supervisor de Zona': 'mdi-account-supervisor',
    'Operador de Rutas': 'mdi-truck',
    'Empleado de Agencia': 'mdi-account-tie-hat',
    'Analista Central': 'mdi-chart-line'
  }
  return icons[role || ''] || 'mdi-account'
}

const formatDate = (date?: Date) => {
  if (!date) return 'No especificado'
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

const toggleEditMode = () => {
  editMode.value = true
  editableUser.value = { ...currentUser.value }
}

const saveChanges = () => {
  if (currentUser.value && editableUser.value) {
    Object.assign(currentUser.value, editableUser.value)
    editMode.value = false
    
    store.showNotification({
      type: 'success',
      title: 'Perfil Actualizado',
      message: 'Los cambios han sido guardados correctamente'
    })
  }
}

const cancelEdit = () => {
  editMode.value = false
  editableUser.value = {}
}

const showCode = () => {
  console.log('💻 Mostrar código del componente UserProfile')
  store.showNotification({
    type: 'info',
    title: 'Código del Componente',
    message: 'Abriendo vista del código fuente de UserProfile...'
  })
}

// 👀 Watchers
watch(selectedUser, () => {
  editMode.value = false
  editableUser.value = {}
})

// 🎬 Lifecycle
onMounted(() => {
  console.log('👤 Demo de UserProfile cargado')
})
</script>

<style scoped>
.user-profile-demo {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.user-profile-card {
  overflow: hidden;
}

.profile-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

.profile-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.profile-content {
  z-index: 1;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #9c27b0 0%, #e91e63 100%);
}

:deep(.v-text-field--variant-outlined) {
  --v-field-border-opacity: 0.3;
}

:deep(.v-text-field--variant-outlined.v-text-field--focused) {
  --v-field-border-opacity: 1;
}
</style>
